import React, { useEffect, useState } from 'react';
import { Box, Text, Input, VStack, InputGroup, InputRightElement, Button, HStack } from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

interface MapSelectorProps {
  onLocationSelect: (lat: number, lng: number, address: any) => void;
  initialLat?: number | null;
  initialLng?: number | null;
}

const MapSelector: React.FC<MapSelectorProps> = ({ onLocationSelect, initialLat, initialLng }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState({
    lat: initialLat || 19.325665,
    lng: initialLng || -98.256841
  });
  const [markerPosition, setMarkerPosition] = useState({
    lat: initialLat || 19.325665,
    lng: initialLng || -98.256841
  });

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const getAddressFromCoords = (lat: number, lng: number) => {
    if (!window.google) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const result = results[0];
        const addressComponents = result.address_components;
        
        const address = {
          fullAddress: result.formatted_address,
          street: '',
          number: '',
          neighborhood: '',
          city: '',
          postalCode: ''
        };

        addressComponents.forEach((component: any) => {
          const types = component.types;
          if (types.includes('street_number')) {
            address.number = component.long_name;
          } else if (types.includes('route')) {
            address.street = component.long_name;
          } else if (types.includes('sublocality') || types.includes('neighborhood')) {
            address.neighborhood = component.long_name;
          } else if (types.includes('locality')) {
            address.city = component.long_name;
          } else if (types.includes('postal_code')) {
            address.postalCode = component.long_name;
          }
        });

        onLocationSelect(lat, lng, address);
      } else {
        onLocationSelect(lat, lng, {
          fullAddress: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          street: '',
          number: '',
          neighborhood: '',
          city: '',
          postalCode: ''
        });
      }
    });
  };

  const searchLocation = () => {
    if (!searchQuery.trim() || !window.google) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        getAddressFromCoords(lat, lng);
      }
    });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          getAddressFromCoords(lat, lng);
        },
        (error) => {
          console.error('Error obteniendo ubicación:', error);
        }
      );
    }
  };

  const handleMarkerDragEnd = (e: any) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    
    if (lat && lng) {
      setMarkerPosition({ lat, lng });
      getAddressFromCoords(lat, lng);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchLocation();
    }
  };

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <VStack spacing={3} align="stretch">
      <InputGroup>
        <Input
          placeholder="Buscar dirección..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          size="sm"
        />
        <InputRightElement width="12rem">
          <HStack spacing={1}>
            <Button h="1.75rem" size="sm" onClick={getCurrentLocation}>
              Ubicación Actual
            </Button>
            <Button h="1.75rem" size="sm" onClick={searchLocation}>
              Buscar
            </Button>
          </HStack>
        </InputRightElement>
      </InputGroup>
      
      <Box h="400px" w="100%">
        <GoogleMap
          zoom={15}
          center={mapCenter}
          mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '8px' }}
        >
          <Marker
            position={markerPosition}
            draggable={true}
            onDragEnd={handleMarkerDragEnd}
          />
        </GoogleMap>
      </Box>
      
      <Text fontSize="sm" color="gray.600">
        Busca una dirección, usa tu ubicación actual o arrastra el marcador para seleccionar la ubicación
      </Text>
    </VStack>
  );
};

export default MapSelector;
